import { createContext, PropsWithChildren, ReactElement, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useForm, UseFormReturn } from '../../../../../hooks'
import { AutomaticPaymentRemindersType } from '../enums/automaticPaymentRemindersType'
import { AutomaticPaymentRemindersModalFormSchema } from '../types/paymentReminders/automaticPaymentRemindersModalFormSchema'
import { PaymentRemindersDetails } from '../types/paymentReminders/paymentRemindersDetails'
import { getAutomaticPaymentRemindersDetailsSchema } from '../utils/getAutomaticPaymentRemindersDetailsSchema'

interface ContextState {
  automaticPaymentRemindersModalForm: UseFormReturn<AutomaticPaymentRemindersModalFormSchema>
  submitForm: () => void
  variant: AutomaticPaymentRemindersType
}

const AutomaticPaymentRemindersModalFormContext = createContext<ContextState | undefined>(undefined)

interface AutomaticPaymentRemindersModalFormProviderProps extends PropsWithChildren {
  defaultValues: PaymentRemindersDetails
  onSubmit: (formData: PaymentRemindersDetails) => void
  variant: AutomaticPaymentRemindersType
}

export const AutomaticPaymentRemindersModalFormContextProvider = ({
  children,
  defaultValues,
  onSubmit,
  variant,
}: AutomaticPaymentRemindersModalFormProviderProps): ReactElement => {
  const { t } = useTranslation()

  const automaticPaymentRemindersModalForm = useForm<AutomaticPaymentRemindersModalFormSchema>({
    defaultValues,
    validationSchema: getAutomaticPaymentRemindersDetailsSchema(t, variant),
  })

  const {
    Form,
    handleSubmit: handleAutomaticPaymentRemindersModalFormSubmit,
    reset: resetForm,
  } = automaticPaymentRemindersModalForm

  useEffect(() => {
    if (defaultValues) {
      resetForm(defaultValues)
    }
  }, [defaultValues, resetForm])

  const submitForm = useCallback(() => {
    handleAutomaticPaymentRemindersModalFormSubmit(onSubmit)()
  }, [handleAutomaticPaymentRemindersModalFormSubmit, onSubmit])

  const contextValue = useMemo(
    () => ({
      automaticPaymentRemindersModalForm,
      submitForm,
      variant,
    }),
    [automaticPaymentRemindersModalForm, submitForm, variant],
  )

  return (
    <AutomaticPaymentRemindersModalFormContext.Provider value={contextValue}>
      <Form>{children}</Form>
    </AutomaticPaymentRemindersModalFormContext.Provider>
  )
}

export const useAutomaticPaymentRemindersModalForm = () => {
  const context = useContext(AutomaticPaymentRemindersModalFormContext)

  if (!context) {
    throw new Error('AutomaticPaymentRemindersModalFormContextProvider is missing in the module!')
  }

  return context
}
